import {identity} from '@raison/toolbox';
import {To} from 'history';
import React, {ComponentProps, PropsWithChildren, ReactNode, useContext, useEffect} from 'react';
import {Link, useNavigate} from 'react-router';
import {useOptionalControls} from '../../Hooks/useOptionalControls';
import {useRootElement} from '../../Utilities/useRootElement';
import * as cls from './RootLayout.css';
import logoSvg from './logo.svg';

interface MenuControls {
    isMenuOpened: boolean;
    setMenuOpened: (isOpen: boolean) => void;
}

export const MenuStateContext = React.createContext<MenuControls | undefined>(undefined);
export const MenuElementContext = React.createContext<React.JSX.Element | null>(null);
const MenuOutlet = () => <MenuElementContext.Consumer children={identity} />;

const RootLayoutContext = React.createContext<RootLayout.SharedProps>({});

function RootLayout({
    common = false,
    layout: layoutName,
    background: backgroundVariantName,
    pageHeader = <RootLayout.TitleHeader />,
    menuHeader = <RootLayout.LogoHeader align='center' />,
    menu = <MenuOutlet />,
    title,
    navigateBack,
    children,
    menuIsOpen,
    onMenuToggle,
}: RootLayout.Props) {
    const [isMenuOpened, setMenuOpened] = useOptionalControls(false, menuIsOpen, onMenuToggle);
    const rootElement = useRootElement();

    useEffect(() => {
        document.title = title || 'Raison app';
    }, [title]);

    useEffect(() => {
        if (isMenuOpened) {
            rootElement.style.setProperty('overflow', 'hidden');
        } else {
            rootElement.style.removeProperty('overflow');
        }
    }, [isMenuOpened]);

    if (common && !layoutName) {
        layoutName = 'common';
    }
    if (layoutName) {
        const Layout = layoutPresets[layoutName];
        children = <Layout>{children}</Layout>;
    }

    let className = cls.main;
    if (backgroundVariantName) {
        className += ` ${cls.backgroundVariants[backgroundVariantName]}`;
    }

    return (
        <MenuStateContext.Provider value={{isMenuOpened, setMenuOpened}}>
            <RootLayoutContext.Provider value={{title, navigateBack}}>
                {isMenuOpened ? menuHeader : pageHeader}
                <main className={className}>{children}</main>
                <nav onClick={() => setMenuOpened(false)} className={isMenuOpened ? cls.menuOpened : cls.menuClosed}>
                    {menu}
                </nav>
            </RootLayoutContext.Provider>
        </MenuStateContext.Provider>
    );
}

namespace RootLayout {
    export interface SharedProps {
        title?: string;
        navigateBack?: NavigateBackProp;
    }

    export interface Props extends SharedProps {
        children?: ReactNode;
        menu?: React.JSX.Element | null;
        pageHeader?: React.JSX.Element | null;
        menuHeader?: React.JSX.Element | null;
        menuIsOpen?: boolean;
        onMenuToggle?: (isOpen: boolean) => void;
        common?: boolean;
        layout?: keyof typeof layoutPresets;
        background?: keyof typeof cls.backgroundVariants;
    }
}

const layoutPresets = {
    common: RootLayoutCommonPreset,
};

function RootLayoutCommonPreset({children}: PropsWithChildren) {
    return <div className={cls.layoutVariant.common}>{children}</div>;
}

/**
 * Just header styles and semantic tag
 */
function BaseHeader({align = 'center', bg = 'fill', ...props}: BaseHeaderProps) {
    return <header {...props} className={`${cls.header} ${cls.headerAlign[align]} ${cls.headerBg[bg]}`} />;
}

function CustomHeader(props: PropsWithChildren) {
    const {navigateBack = -1} = useContext(RootLayoutContext)!;
    return (
        <BaseHeader>
            <MenuButton />
            {props.children}
            {renderBackNavigation(navigateBack)}
        </BaseHeader>
    );
}

interface BaseHeaderProps {
    children?: ReactNode;
    align?: keyof typeof cls.headerAlign;
    bg?: keyof typeof cls.headerBg;
}

/**
 * Header for most pages with ability to opt-out of menu button.
 * The prop `navigateBack` is multifunctional and it is this way for convenience
 */
function TitleHeader({noMenuButton, ...rest}: TitleHeaderProps) {
    const {title, navigateBack = -1} = useContext(RootLayoutContext)!;
    return (
        <BaseHeader {...rest}>
            {noMenuButton ? null : <MenuButton />}
            {title && <TitleOutlet />}
            {renderBackNavigation(navigateBack)}
        </BaseHeader>
    );
}

interface TitleHeaderProps extends BaseHeaderProps {
    noMenuButton?: boolean;
}

type NavigateBackProp = false | string | number | ((goBack: () => void) => void);

const renderBackNavigation = (navigateBack: NavigateBackProp): React.JSX.Element | null => {
    if (navigateBack === false) {
        return null;
    }
    if (typeof navigateBack === 'string') {
        return <BackLink to={navigateBack} />;
    }
    if (typeof navigateBack === 'number') {
        return <BackButton go={navigateBack} />;
    }
    {
        return <BackButton onClick={navigateBack} />;
    }
};

/**
 * Assumes that menu button is always needed since this header will likely be used as `menuHeader`
 * This also implies lack of back button
 */
function LogoHeader({children, align = 'left'}: LogoHeaderProps) {
    return (
        <BaseHeader align={align}>
            <MenuButton />
            <CompanyLogo />
            {children}
        </BaseHeader>
    );
}

type LogoHeaderProps = BaseHeaderProps;

/**
 * Encapsulates control over menu
 */
function MenuButton() {
    const controls = useContext(MenuStateContext);
    return (
        <button
            type='button'
            onClick={() => controls?.setMenuOpened(!controls.isMenuOpened)}
            className={cls.menuButton}
        >
            Menu
        </button>
    );
}

/**
 * Provided onClick overrides default action but receives it as a callback and
 * can elect to invoke it or not
 */
function BackButton({go = -1, onClick = goBack => goBack()}: {go?: number; onClick?: (goBack: () => void) => void}) {
    const navigate = useNavigate();
    return (
        <button type='button' onClick={() => onClick(() => navigate(go))} className={cls.backButton}>
            Back
        </button>
    );
}

/**
 * For cases where previous page is explicitly known, by default will replace history state
 */
function BackLink({to, replace = true}: {to: To; replace?: boolean}) {
    return (
        <Link to={to} replace={replace} className={cls.backButton}>
            Back
        </Link>
    );
}

/**
 * Just title styles; there's both `children` and `text` simply for syntactic sugar
 */
function TitleOutlet(props: {multiline?: boolean}) {
    const {title} = useContext(RootLayoutContext)!;
    let className = cls.titleText.default;
    if (props.multiline) {
        className = cls.titleText.multiline;
    }
    return <h1 className={className}>{title}</h1>;
}

/**
 * Raison logo as a Link to "/"
 */
function CompanyLogo() {
    return <Link to='/' className={cls.companyLogo} />;
}

function CompanyLogoImg(props: Omit<ComponentProps<'img'>, 'src' | 'alt'>) {
    return <img {...props} src={logoSvg} alt='Raison logo' />;
}

CompanyLogo.className = cls.companyLogo;

RootLayout.CustomHeader = CustomHeader;
RootLayout.BaseHeader = BaseHeader;
RootLayout.TitleHeader = TitleHeader;
RootLayout.LogoHeader = LogoHeader;
RootLayout.MenuButton = MenuButton;
RootLayout.BackButton = BackButton;
RootLayout.BackLink = BackLink;
RootLayout.TitleOutlet = TitleOutlet;
RootLayout.CompanyLogo = CompanyLogo;
RootLayout.CompanyLogoImg = CompanyLogoImg;

export default RootLayout;
